import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowUpTrayIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  EntityLink,
  ErrorSummary,
  Input,
  InsurerLogo,
  Modal,
  ModalFooter,
  Pagination,
  SearchableSelect,
  Select,
  getStatusBadgeVariant,
} from '@/components/common';
import { policyNumberLabel } from '@/components/common/PolicyNumber';
import api, { getErrorMessage } from '@/services/api';
import { useInsurer } from '@/hooks/useInsurers';
import { useCustomers } from '@/hooks/useCustomers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { usePolicies } from '@/hooks/usePolicies';
import {
  useAllInsuranceGroupsLookup,
  useInsuranceSubgroupsLookup,
  useInsuranceTypesLookup,
  usePolicyTypesLookup,
  useProductsLookup,
} from '@/hooks/useProducts';
import { classificationFieldState } from '@/hooks/classificationField';
import {
  type BandAxis,
  bandView,
  editBand,
  editNcbChoice,
  initialToTouched,
  ncbChoice,
} from './rateFormBands';
import {
  useFuelTypesLookup,
  useVehicleMakes,
  useVehicleModels,
  useVehicleSegments,
  useVehicleTypesLookup,
  useVehicleVariants,
} from '@/hooks/useVehicles';
import { useStateLookup, useCityLookup, usePincodes, useRTOLookup } from '@/hooks/useGeography';
import { getFieldErrors } from '@/services/api';
import { validateBrokerRateForm, type BrokerRateFormState } from './brokerRateForm';
import { validateAddRate } from './addRateValidation';
import { SLAB_BASIS_LABELS, slabCriteriaSummary } from '../commissions/slabTypes';
import {
  useBrokerRates,
  useCreateBrokerRate,
  useUpdateBrokerRate,
  useDeleteBrokerRate,
  useUploadBrokerRatesCsv,
  useInsurerInsuranceTypes,
  useCommissionSlabs,
  useCreateSlab,
  useUpdateSlab,
  useDeleteSlab,
  useCommissionWaterfalls,
} from '@/hooks/useCommissions';
import { commissionService } from '@/services';
import { formatCurrency, formatDate, titleCase } from '@/utils';
import type {
  BrokerCommissionRateRecord,
  BrokerRateCsvUploadResult,
  CommissionSlabRecord,
  SlabBasis,
} from '@/types';

type TabId = 'policies' | 'rates' | 'upload' | 'slabs' | 'waterfall' | 'history';
const tabs: { id: TabId; label: string }[] = [
  { id: 'policies', label: 'Policies' },
  { id: 'rates', label: 'Commissions' },
  { id: 'upload', label: 'Upload Grid' },
  { id: 'slabs', label: 'Slabs' },
  { id: 'waterfall', label: 'Waterfall' },
  { id: 'history', label: 'History' },
];

const premiumBaseOptions = [
  { value: 'net_premium', label: 'Net Premium' },
  { value: 'gross_premium', label: 'Gross Premium' },
  { value: 'od_premium', label: 'OD Premium' },
  { value: 'tp_premium', label: 'TP Premium' },
];

export function BrokerInsurerCommissionsPage() {
  const { id: insurerId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = (tabs.find((t) => t.id === searchParams.get('tab'))?.id || 'policies') as TabId;
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const { data: insurer } = useInsurer(insurerId || '');

  return (
    <div>
      <PageHeader
        // U-03: the company's mark beside its name, and its own blurb as the
        // subtitle. `description` is EMPTY on every live insurer today (nothing
        // could write it until this batch), so the subtitle is deliberately
        // omitted rather than rendering an empty line under the title.
        title={
          <span className="flex items-center gap-3">
            <InsurerLogo src={insurer?.logo_display_url} name={insurer?.name} size="md" />
            <span>{insurer ? insurer.name : 'Insurance Company'}</span>
          </span>
        }
        subtitle={insurer?.description || undefined}
        breadcrumbs={[
          { label: 'Insurance Companies', href: '/broker/insurers' },
          { label: insurer?.name || 'Insurer' },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <Link to={`/policies?insurer=${insurerId}`}>
              <Button variant="outline">My Policy</Button>
            </Link>
            <Link to="/broker/insurers">
              <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-4 h-4" />}>
                Back
              </Button>
            </Link>
          </div>
        }
      />

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex -mb-px space-x-8">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'policies' && (
        <PoliciesTab insurerId={insurerId || ''} />
      )}
      {activeTab === 'rates' && (
        <RatesTab insurerId={insurerId || ''} insurerName={insurer?.name || ''} />
      )}
      {activeTab === 'upload' && <UploadTab insurerId={insurerId || ''} insurerName={insurer?.name || ''} />}
      {activeTab === 'slabs' && <SlabsTab insurerId={insurerId || ''} />}
      {activeTab === 'waterfall' && <WaterfallTab insurerId={insurerId || ''} />}
      {activeTab === 'history' && <HistoryTab insurerId={insurerId || ''} />}
    </div>
  );
}

// =====================================================================
// Policies tab — insurer-scoped policy access (enable/disable for selling)
// =====================================================================

/** A row of GET /insurers/insurers/{id}/available-tree/ (Task 3.2). Flat — one
 *  row per mapped `insurers.InsurancePolicy`, carrying its Product's
 *  classification as nested {id, name} labels instead of the old nested tree.
 *  ALL mapped, non-deleted products are returned (is_active/is_visible_to_all
 *  are no longer used to hide rows — they're surfaced as status instead), so
 *  this is the full broker-management catalogue for the insurer. */
interface AvailableProduct {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  is_visible_to_all: boolean;
  access_status: string;
  insurance_type: { id: string; name: string } | null;
  group: { id: string; name: string } | null;
  subgroup: { id: string; name: string } | null;
}

interface PaginatedAvailableProducts {
  count: number;
  next: string | null;
  previous: string | null;
  results: AvailableProduct[];
}

/** Product access has exactly two states now: available (the default — nothing to
 *  request) or disabled (the broker or the insurer switched it off). */
const isDisabled = (accessStatus: string) => accessStatus === 'disabled';

const accessBadgeVariant = (accessStatus: string): 'success' | 'default' =>
  isDisabled(accessStatus) ? 'default' : 'success';

const accessStatusLabel = (accessStatus: string) =>
  isDisabled(accessStatus) ? 'Disabled' : 'Available';

const pageSizeOptions = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
];

function PoliciesTab({ insurerId }: { insurerId: string }) {
  const [subTab, setSubTab] = useState<'my' | 'available'>('my');

  // ── "My Policies" (Task 3.1) — ISSUED policies.Policy rows for this insurer,
  // not product offerings. Default page size 10, selectable 10/50.
  const [issuedPage, setIssuedPage] = useState(1);
  const [issuedPageSize, setIssuedPageSize] = useState(10);
  const {
    data: issuedData,
    isLoading: issuedLoading,
    refetch: refetchIssued,
  } = usePolicies(
    issuedPage,
    { insurer: insurerId, issued: true, page_size: issuedPageSize },
    { enabled: subTab === 'my' && !!insurerId },
  );
  const issuedPolicies = issuedData?.results ?? [];
  const issuedTotal = issuedData?.count ?? 0;
  const issuedTotalPages = Math.max(1, Math.ceil(issuedTotal / issuedPageSize));

  const handleIssuedPageSizeChange = (value: string) => {
    setIssuedPageSize(Number(value));
    setIssuedPage(1);
  };

  // Edit detail & SEO / Enable / Disable — Task 3.3 moved these onto the
  // "Available Products" rows, the tree they actually govern access for.
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  // ── "Available Products" (Task 3.2) — ALL mapped, non-deleted products for
  // this insurer (is_visible_to_all=False products are NO LONGER hidden; their
  // status is surfaced instead). Flat, paginated list: default page size 10,
  // selectable 10/50.
  const [availablePage, setAvailablePage] = useState(1);
  const [availablePageSize, setAvailablePageSize] = useState(10);
  const [availableData, setAvailableData] = useState<PaginatedAvailableProducts | null>(null);

  const fetchAvailable = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/insurers/insurers/${insurerId}/available-tree/`, {
        params: { page: availablePage, page_size: availablePageSize },
      });
      setAvailableData(res.data as PaginatedAvailableProducts);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subTab === 'available') fetchAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, insurerId, availablePage, availablePageSize]);

  const availableProducts = availableData?.results ?? [];
  const availableTotal = availableData?.count ?? 0;
  const availableTotalPages = Math.max(1, Math.ceil(availableTotal / availablePageSize));

  const handleAvailablePageSizeChange = (value: string) => {
    setAvailablePageSize(Number(value));
    setAvailablePage(1);
  };

  // The broker's only lever on a product: switch it off, or back on. Re-enabling a
  // product the INSURER disabled is refused server-side (403) — the toast says so.
  // Only reachable from Available Products rows, so the refresh target is always
  // that list.
  const togglePolicy = async (policyId: string, enable: boolean) => {
    setToggling(policyId);
    try {
      await api.post('/brokers/policy-requests/toggle_policy/', { policy_id: policyId, enable });
      toast.success(enable ? 'Product enabled for selling' : 'Product disabled');
      fetchAvailable();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setToggling(null);
    }
  };

  return (
    <Card padding="none">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex gap-2">
          <Button
            variant={subTab === 'my' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => { setIssuedPage(1); setSubTab('my'); }}
          >
            My Policies
          </Button>
          <Button
            variant={subTab === 'available' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setSubTab('available')}
          >
            Available Products
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (subTab === 'my' ? refetchIssued() : fetchAvailable())}
        >
          Refresh
        </Button>
      </div>

      {subTab === 'my' ? (
        <div>
          {issuedLoading ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          ) : issuedPolicies.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No policies have been issued for this insurer yet.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end gap-2 px-4 pt-3">
                <label htmlFor="my-policies-page-size" className="text-xs text-gray-500 dark:text-gray-400">
                  Rows per page
                </label>
                <div className="w-20">
                  <Select
                    id="my-policies-page-size"
                    value={String(issuedPageSize)}
                    onChange={(e) => handleIssuedPageSizeChange(e.target.value)}
                    options={pageSizeOptions}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {['Policy Number', 'Holder', 'Premium', 'Inception', 'Expiry', 'Status'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {issuedPolicies.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-3 text-sm font-medium">
                          <Link
                            to={`/policies/${p.id}`}
                            className="text-primary-700 dark:text-primary-300 hover:underline"
                          >
                            {policyNumberLabel(p)}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <p className="text-gray-900 dark:text-gray-100">{p.holder_name}</p>
                          {p.holder_phone && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{p.holder_phone}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(p.total_premium)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{formatDate(p.inception_date)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{formatDate(p.expiry_date)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(p.status)} size="sm">
                            {titleCase(p.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {issuedTotalPages > 1 && (
                <Pagination
                  currentPage={issuedPage}
                  totalPages={issuedTotalPages}
                  totalItems={issuedTotal}
                  itemsPerPage={issuedPageSize}
                  onPageChange={setIssuedPage}
                />
              )}
            </>
          )}
        </div>
      ) : loading ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : availableProducts.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No products are mapped to this insurer yet.
        </div>
      ) : (
        // Task 3.2: flat, paginated list of ALL mapped, non-deleted products for
        // this insurer. is_visible_to_all=False products are shown (their status
        // is surfaced, not hidden). Task 3.3: Edit detail & SEO + Enable/Disable
        // live here now — this is the tree the broker actually manages access on.
        <>
          <div className="flex items-center justify-end gap-2 px-4 pt-3">
            <label htmlFor="available-page-size" className="text-xs text-gray-500 dark:text-gray-400">
              Rows per page
            </label>
            <div className="w-20">
              <Select
                id="available-page-size"
                value={String(availablePageSize)}
                onChange={(e) => handleAvailablePageSizeChange(e.target.value)}
                options={pageSizeOptions}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Product', 'Type', 'Group / Subgroup', 'Status', ''].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {availableProducts.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm">
                      <button
                        type="button"
                        onClick={() => setEditingPolicyId(p.id)}
                        className="font-medium text-primary-700 dark:text-primary-300 hover:underline text-left"
                      >
                        {p.name}
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{p.code}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {p.insurance_type?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {p.group?.name || '—'}
                      {p.subgroup?.name ? ` / ${p.subgroup.name}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={accessBadgeVariant(p.access_status)} size="sm">
                          {accessStatusLabel(p.access_status)}
                        </Badge>
                        {!p.is_active && (
                          <Badge variant="default" size="sm">
                            Inactive
                          </Badge>
                        )}
                        {!p.is_visible_to_all && (
                          <Badge variant="warning" size="sm">
                            Restricted
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* The management endpoints (toggle_policy / policy_seo) resolve the
                          product through mapped_policies_for(broker), which requires
                          is_active AND is_visible_to_all. Inactive/Restricted rows are
                          surfaced (Task 3.2) but the insurer has taken them out of the
                          broker's manageable set — acting on them 404s server-side, so the
                          controls are disabled here to match those same badge conditions. */}
                      {!p.is_active || !p.is_visible_to_all ? (
                        <span
                          className="text-xs text-gray-400 dark:text-gray-500 italic"
                          title="Insurer has restricted or deactivated this product — not manageable"
                        >
                          Not manageable
                        </span>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingPolicyId(p.id)}>
                            Edit detail &amp; SEO
                          </Button>
                          {isDisabled(p.access_status) ? (
                            <Button
                              variant="primary"
                              size="sm"
                              isLoading={toggling === p.id}
                              onClick={() => togglePolicy(p.id, true)}
                            >
                              Enable
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              isLoading={toggling === p.id}
                              onClick={() => togglePolicy(p.id, false)}
                            >
                              Disable
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {availableTotalPages > 1 && (
            <Pagination
              currentPage={availablePage}
              totalPages={availableTotalPages}
              totalItems={availableTotal}
              itemsPerPage={availablePageSize}
              onPageChange={setAvailablePage}
            />
          )}
        </>
      )}

      {editingPolicyId && (
        <PolicySeoModal
          policyId={editingPolicyId}
          onClose={() => setEditingPolicyId(null)}
          onSaved={fetchAvailable}
        />
      )}
    </Card>
  );
}

// =====================================================================
// Policy detail + SEO editor (broker override of insurer defaults)
// =====================================================================

interface PolicySeoFields {
  name: string;
  description: string;
  seo_meta_title: string;
  seo_meta_description: string;
  seo_slug: string;
}

interface PolicySeoData {
  policy_name: string;
  insurer_name: string;
  default: PolicySeoFields;
  override: PolicySeoFields;
  effective: PolicySeoFields;
}

const SEO_FIELD_DEFS: { key: keyof PolicySeoFields; label: string; type: 'text' | 'textarea' }[] = [
  { key: 'name', label: 'Display name', type: 'text' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'seo_meta_title', label: 'SEO meta title', type: 'text' },
  { key: 'seo_meta_description', label: 'SEO meta description', type: 'textarea' },
  { key: 'seo_slug', label: 'URL slug', type: 'text' },
];

const emptySeoFields: PolicySeoFields = {
  name: '',
  description: '',
  seo_meta_title: '',
  seo_meta_description: '',
  seo_slug: '',
};

function PolicySeoModal({
  policyId,
  onClose,
  onSaved,
}: {
  policyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<PolicySeoData | null>(null);
  const [form, setForm] = useState<PolicySeoFields>(emptySeoFields);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/brokers/policy-requests/policy_seo/', {
          params: { policy_id: policyId },
        });
        if (!active) return;
        setData(res.data);
        setForm({ ...emptySeoFields, ...res.data.override });
      } catch (error) {
        toast.error(getErrorMessage(error));
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post('/brokers/policy-requests/save_policy_seo/', {
        policy_id: policyId,
        ...form,
      });
      setData(res.data);
      toast.success('Saved for your domain');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const useDefault = async () => {
    setResetting(true);
    try {
      const res = await api.post('/brokers/policy-requests/reset_policy_seo/', {
        policy_id: policyId,
      });
      setData(res.data);
      setForm({ ...emptySeoFields });
      toast.success('Reverted to insurer defaults');
      onSaved();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setResetting(false);
    }
  };

  const setField = (key: keyof PolicySeoFields, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={data ? `Edit: ${data.policy_name}` : 'Edit policy detail & SEO'}
      size="lg"
    >
      {loading || !data ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Customise how this policy appears on <strong>your domain</strong>. Leave a field blank to
            use the insurer&apos;s default (shown in grey). Use <strong>Use Default</strong> to clear
            all of your overrides.
          </p>
          {SEO_FIELD_DEFS.map((f) => (
            <div key={f.key}>
              {f.type === 'textarea' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {f.label}
                  </label>
                  <textarea
                    rows={3}
                    value={form[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={data.default[f.key] || '—'}
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              ) : (
                <Input
                  label={f.label}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={data.default[f.key] || '—'}
                />
              )}
              {data.default[f.key] && (
                <p className="mt-1 text-xs text-gray-400">Default: {data.default[f.key]}</p>
              )}
            </div>
          ))}
        </div>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={useDefault} isLoading={resetting} disabled={loading}>
          Use Default
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} isLoading={saving} disabled={loading}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// =====================================================================
// Rates tab
// =====================================================================

function RatesTab({ insurerId, insurerName }: { insurerId: string; insurerName: string }) {
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<BrokerCommissionRateRecord | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useBrokerRates(page, { insurer: insurerId, is_active: 'true' });
  const deleteMutation = useDeleteBrokerRate();

  const rows = data?.results || [];

  return (
    <Card padding="none">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {insurerName ? (
              <>
                Commission rates —{' '}
                <EntityLink kind="insurer" id={insurerId} name={insurerName} />
              </>
            ) : (
              'Active commission rates for this insurer.'
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Click a row to see full dimension detail.
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<PlusIcon className="w-4 h-4" />}
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
        >
          Add Rate
        </Button>
      </div>
      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No active rates yet. Click <strong>Add Rate</strong> to enter one, or use the{' '}
          <strong>Upload Grid</strong> tab to import the insurer&apos;s monthly grid.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['Scope', 'Premium Base', 'OD / TP', 'Percentage', 'Flat', 'Reward', 'Max Cap', 'Effective', 'Status', ''].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((r) => {
                const isExpanded = expandedId === r.id;
                // Collect set dimension names for the expandable detail row
                const dims: { label: string; value: string }[] = [];
                // Item 10 first in the list: a customer-scoped rate replaces the
                // broker's whole inflow for that person, so it is the one scope
                // that must never be read as just another motor filter.
                if (r.customer_name) dims.push({ label: 'Customer', value: r.customer_name });
                if (r.product_name) dims.push({ label: 'Product', value: r.product_name });
                if (r.insurance_type_name) dims.push({ label: 'Type', value: r.insurance_type_name });
                if (r.group_name) dims.push({ label: 'Group', value: r.group_name });
                if (r.subgroup_name) dims.push({ label: 'Subgroup', value: r.subgroup_name });
                if (r.policy_type_name) dims.push({ label: 'Policy Type', value: r.policy_type_name });
                if (r.vehicle_type_name) dims.push({ label: 'Vehicle Type', value: r.vehicle_type_name });
                if (r.make_name) dims.push({ label: 'Make', value: r.make_name });
                if (r.model_name) dims.push({ label: 'Model', value: r.model_name });
                if (r.state_name) dims.push({ label: 'State', value: r.state_name });
                if (r.city_name) dims.push({ label: 'City', value: r.city_name });
                // Every remaining criterion the rate can carry. A filter the
                // broker cannot see here is one they cannot reconcile against a
                // commission that came out lower than they expected — which is
                // how the CC / seating / RTO scopes went unnoticed for so long.
                pushCriterion(dims, r, 'rto_name', 'RTO');
                pushCriterion(dims, r, 'pincode_code', 'Pincode');
                pushCriterion(dims, r, 'zip_code', 'ZIP');
                pushCriterion(dims, r, 'fuel_type_name', 'Fuel');
                pushCriterion(dims, r, 'variant_name', 'Variant');
                pushCriterion(dims, r, 'manufactured_year', 'Mfg Year');
                pushRange(dims, r, 'CC', 'cc', 'cc_from', 'cc_to');
                pushRange(dims, r, 'GVW', 'gvw', 'gvw_from', 'gvw_to');
                pushRange(dims, r, 'Age', 'age', 'age_from', 'age_to');
                pushRange(
                  dims,
                  r,
                  'Seating',
                  'seating_capacity',
                  'seating_capacity_from',
                  'seating_capacity_to',
                );
                pushCriterion(dims, r, 'ncb', 'NCB %');
                const rateCondition = criterion(r, 'vehicle_condition');
                if (rateCondition) {
                  dims.push({ label: 'Vehicle Condition', value: titleCase(rateCondition) });
                }
                const rateNcbApplicable = ncbApplicableFrom(r);
                if (rateNcbApplicable) {
                  dims.push({ label: 'NCB', value: rateNcbApplicable === 'yes' ? 'Yes' : 'No' });
                }
                const rateCategory = criterion(r, 'vehicle_category');
                if (rateCategory) {
                  dims.push({
                    label: 'Vehicle Category',
                    // Stored as the code; shown as the label the form offers.
                    value:
                      vehicleCategoryOptions.find((o) => o.value === rateCategory)?.label ||
                      rateCategory,
                  });
                }
                pushCriterion(dims, r, 'vehicle_segment', 'Vehicle Segment');

                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    >
                      <td className="px-4 py-3 text-sm">
                        {r.product_name || r.insurance_type_name || '—'}
                        {/* Item 10. Two rates for the same insurance type, one at
                            10% and one at 20%, are indistinguishable without this
                            -- and the 20% one is somebody's negotiated rate. */}
                        {r.customer_name && (
                          <span className="mt-1 block text-xs font-medium text-indigo-700 dark:text-indigo-300">
                            Customer: {r.customer_name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{titleCase(r.premium_base)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {r.od || r.tp ? `${r.od || '—'} / ${r.tp || '—'}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{r.commission_percentage}%</td>
                      <td className="px-4 py-3 text-sm">{r.commission_flat || '—'}</td>
                      <td className="px-4 py-3 text-sm">{r.reward_percentage || '—'}%</td>
                      <td className="px-4 py-3 text-sm">{r.max_commission || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(r.effective_from)}
                        {r.effective_until ? ` → ${formatDate(r.effective_until)}` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={r.is_active ? 'success' : 'default'} size="sm">
                          {r.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(r);
                            setShowModal(true);
                          }}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Remove this commission rate?`)) deleteMutation.mutate(r.id);
                          }}
                        >
                          <TrashIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${r.id}-detail`} className="bg-gray-50 dark:bg-gray-800/60">
                        <td colSpan={10} className="px-6 py-3">
                          {dims.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No dimension filters set on this rate.</p>
                          ) : (
                            <div className="flex flex-wrap gap-3">
                              {dims.map((d) => (
                                <span key={d.label} className="text-xs text-gray-600 dark:text-gray-300">
                                  <span className="font-medium text-gray-500 dark:text-gray-400">{d.label}:</span>{' '}
                                  {d.value}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.count > 20 && (
        <div className="flex items-center justify-between p-4 text-sm border-t border-gray-100 dark:border-gray-700">
          <span>Page {page}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={rows.length < 20}>
              Next
            </Button>
          </div>
        </div>
      )}

      {showModal && (
        <RateFormModal
          insurerId={insurerId}
          existing={editing}
          onClose={() => setShowModal(false)}
        />
      )}
    </Card>
  );
}

const emptySpecificForm: BrokerRateFormState = {
  vehicle_type: '',
  group: '',
  subgroup: '',
  policy_type: '',
  effective_from: new Date().toISOString().slice(0, 10),
  effective_until: '',
  rto: '',
  cc: '',
  gvw: '',
  fuel_type: '',
  od: '',
  tp: '',
  reward_to: '',
  reward_od: '',
  state: '',
  city: '',
  pincode: '',
  age: '',
  manufactured_year: '',
  seating_capacity: '',
  make: '',
  model: '',
  variant: '',
};

/**
 * Board #199 added optional criteria columns to BrokerCommissionRate that the
 * shared `BrokerCommissionRateRecord` type does not carry yet. This reads one
 * off an existing record for the edit-mode prefill without widening a type
 * this file does not own; a missing key simply prefills blank.
 */
function criterion(record: BrokerCommissionRateRecord | null, key: string): string {
  if (!record) return '';
  const value = (record as unknown as Record<string, unknown>)[key];
  return value === null || value === undefined ? '' : String(value);
}

/** `ncb_applicable` is a nullable boolean server-side, a tri-state select here. */
function ncbApplicableFrom(record: BrokerCommissionRateRecord | null): string {
  if (!record) return '';
  const value = (record as unknown as Record<string, unknown>).ncb_applicable;
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
}

/**
 * B10. A prefilled select whose options have not loaded yet -- or whose lookup
 * does not list the stored value (an insurer that no longer maps the type, a
 * make outside the first page) -- renders as its first option in the DOM. The
 * edit then LOOKS as if the rate had no such filter. Keep the stored value
 * visible, labelled from the rate's own `*_name`, until the real option arrives.
 * Only the value the rate was saved with gets this; a fresh pick always comes
 * from the options themselves.
 */
function withStored(
  options: { value: string; label: string }[],
  value: string,
  storedValue: string | null | undefined,
  storedLabel: string | null | undefined,
): { value: string; label: string }[] {
  if (!value || value !== storedValue || options.some((o) => o.value === value)) return options;
  return [{ value, label: storedLabel || value }, ...options];
}

// The shared `Select` renders its `placeholder` as a DISABLED option, so a
// select that only has one cannot be put back to blank once a value is picked.
// These criteria are optional, and "any" has to stay reachable — otherwise a
// filter set by mistake is a filter the broker can never take off the rate.
const vehicleConditionOptions = [
  { value: '', label: 'Any (new or old)' },
  { value: 'new', label: 'New' },
  { value: 'old', label: 'Old' },
];

type RateDimension = { label: string; value: string };

/** Push one plain criterion onto the rate's dimension list, when it is set. */
function pushCriterion(
  dims: RateDimension[],
  record: BrokerCommissionRateRecord,
  key: string,
  label: string,
): void {
  const value = criterion(record, key);
  if (value) dims.push({ label, value });
}

/**
 * Push a rate's band for one measure: an exact value, a closed band, or an
 * open-ended one. Rendered as what it is — "1000–1500", "1500+", "up to 1500" —
 * so an open end is never mistaken for a value somebody forgot to fill.
 */
function pushRange(
  dims: RateDimension[],
  record: BrokerCommissionRateRecord,
  label: string,
  exactKey: string,
  fromKey: string,
  toKey: string,
): void {
  const exact = criterion(record, exactKey);
  if (exact) dims.push({ label, value: exact });
  const from = criterion(record, fromKey);
  const to = criterion(record, toKey);
  if (from && to) dims.push({ label: `${label} range`, value: `${from} – ${to}` });
  else if (from) dims.push({ label: `${label} range`, value: `${from}+` });
  else if (to) dims.push({ label: `${label} range`, value: `up to ${to}` });
}

/** Mirrors `VehicleModel.CATEGORY_CHOICES` in backend/apps/vehicles/models.py.
 *  A rate scoped on a category matches the category of the model the policy
 *  points at; blank matches any. */
const vehicleCategoryOptions = [
  { value: '', label: 'Any category' },
  { value: '2w', label: 'Two Wheeler' },
  { value: '4w_private', label: 'Private Car' },
  { value: '4w_taxi', label: 'Taxi/Commercial Car' },
  { value: 'cv_gcv', label: 'Goods Carrying Vehicle' },
  { value: 'cv_pcv', label: 'Passenger Carrying Vehicle' },
  { value: 'cv_misc', label: 'Miscellaneous Vehicle' },
];

// #406: one NCB question. "With NCB" reveals the optional NCB % box.
const ncbApplicableOptions = [
  { value: '', label: 'Any' },
  { value: 'no', label: 'No NCB' },
  { value: 'yes', label: 'With NCB' },
];

/**
 * The Motor criteria the Add Rate modal collects, as the form holds them:
 * strings, blank when unset. Structural, so the modal's inferred form object
 * satisfies it without the whole state having to be typed.
 */
export type MotorCriteriaFields = {
  rto: string;
  state: string;
  city: string;
  pincode: string;
  zip_code: string;
  fuel_type: string;
  make: string;
  model: string;
  variant: string;
  cc: string;
  gvw: string;
  ncb: string;
  age: string;
  manufactured_year: string;
  seating_capacity: string;
  od_premium_pct: string;
  tp_premium_pct: string;
  od_reward_pct: string;
  tp_reward_pct: string;
  cc_from: string;
  cc_to: string;
  gvw_from: string;
  gvw_to: string;
  age_from: string;
  age_to: string;
  seating_capacity_from: string;
  seating_capacity_to: string;
  vehicle_condition: string;
  ncb_applicable: string;
  vehicle_category: string;
  vehicle_segment: string;
};

/**
 * Every Motor criterion, cleared.
 *
 * Sent when an existing rate stops being Motor-scoped, so the criteria it used
 * to carry are actually removed instead of lingering on a rate that no longer
 * shows them.
 */
export function blankMotorCriteria(): Record<string, unknown> {
  return {
    rto: null,
    state: null,
    city: null,
    pincode: null,
    zip_code: '',
    fuel_type: null,
    make: null,
    model: null,
    variant: null,
    cc: null,
    gvw: null,
    ncb: null,
    age: null,
    manufactured_year: null,
    seating_capacity: null,
    od: null,
    tp: null,
    od_reward: null,
    tp_reward: null,
    cc_from: null,
    cc_to: null,
    gvw_from: null,
    gvw_to: null,
    age_from: null,
    age_to: null,
    seating_capacity_from: null,
    seating_capacity_to: null,
    vehicle_condition: '',
    ncb_applicable: null,
    vehicle_category: '',
    vehicle_segment: '',
  };
}

/**
 * Every Motor criterion the modal collects, as a value or as an EXPLICIT null.
 * Never omitted when blank.
 *
 * `if (form.cc) payload.cc = ...`, which is how this payload used to be built,
 * looks right on create and is wrong on edit: the update is a PATCH, so a
 * criterion the user CLEARED is simply absent from the body and keeps its
 * stored value. The modal closed on a success toast having cleared nothing,
 * and the rate went on being filtered by a criterion it no longer showed.
 *
 * `"0"` is a truthy string, so a legitimate zero bound survives these guards;
 * only an empty box becomes null.
 */
export function motorCriteriaPayload(form: MotorCriteriaFields): Record<string, unknown> {
  return {
    rto: form.rto || null,
    state: form.state || null,
    city: form.city || null,
    pincode: form.pincode || null,
    zip_code: form.zip_code || '',
    fuel_type: form.fuel_type || null,
    make: form.make || null,
    model: form.model || null,
    variant: form.variant || null,
    cc: form.cc ? Number(form.cc) : null,
    gvw: form.gvw ? Number(form.gvw) : null,
    ncb: form.ncb ? Number(form.ncb) : null,
    age: form.age ? Number(form.age) : null,
    manufactured_year: form.manufactured_year ? Number(form.manufactured_year) : null,
    seating_capacity: form.seating_capacity ? Number(form.seating_capacity) : null,
    // Form names → backend field names.
    od: form.od_premium_pct || null,
    tp: form.tp_premium_pct || null,
    od_reward: form.od_reward_pct || null,
    tp_reward: form.tp_reward_pct || null,
    cc_from: form.cc_from ? Number(form.cc_from) : null,
    cc_to: form.cc_to ? Number(form.cc_to) : null,
    gvw_from: form.gvw_from || null,
    gvw_to: form.gvw_to || null,
    age_from: form.age_from ? Number(form.age_from) : null,
    age_to: form.age_to ? Number(form.age_to) : null,
    seating_capacity_from: form.seating_capacity_from ? Number(form.seating_capacity_from) : null,
    seating_capacity_to: form.seating_capacity_to ? Number(form.seating_capacity_to) : null,
    vehicle_condition: form.vehicle_condition || '',
    ncb_applicable: form.ncb_applicable ? form.ncb_applicable === 'yes' : null,
    vehicle_category: form.vehicle_category || '',
    vehicle_segment: form.vehicle_segment || '',
  };
}

function RateFormModal({
  insurerId,
  existing,
  onClose,
}: {
  insurerId: string;
  existing: BrokerCommissionRateRecord | null;
  onClose: () => void;
}) {
  // QA round 4: new rates use the simplified insurance-type form. The legacy
  // specific-product branch is only reachable when editing a pre-existing
  // product-scoped rate that has NO insurance type (no scope toggle is shown).
  //
  // B10: this used to key on `existing.product` alone. #249 saves a product ON
  // an insurance-type rate, so every such rate reopened in the legacy branch,
  // showed its Motor criteria blank, and Save sent `insurance_type: null,
  // product: null` -- silently rescoping it. A rate with an insurance type is
  // an insurance-type rate, whatever else it carries.
  const [scopeMode] = useState<'product' | 'insurance_type'>(
    existing?.product && !existing.insurance_type ? 'product' : 'insurance_type',
  );
  // Add Rate dropdown shows only the insurance types mapped to this insurer.
  const { data: insurerTypes } = useInsurerInsuranceTypes(insurerId);
  const createMutation = useCreateBrokerRate();
  const updateMutation = useUpdateBrokerRate();
  const [form, setForm] = useState({
    product: existing?.product || '',
    insurance_type: existing?.insurance_type || '',
    // Item 10 — blank on an ordinary rate; a contact id makes this rate the
    // broker's inflow for that one person with this insurer.
    customer: existing?.customer || '',
    commission_percentage: existing?.commission_percentage || '',
    commission_flat: existing?.commission_flat || '0',
    reward_percentage: existing?.reward_percentage || '0',
    premium_base: existing?.premium_base || 'net_premium',
    max_commission: existing?.max_commission || '',
    effective_from: existing?.effective_from || new Date().toISOString().slice(0, 10),
    effective_until: existing?.effective_until || '',
    // Expanded insurance-type form fields (Task I1)
    group: existing?.group || '',
    subgroup: existing?.subgroup || '',
    policy_type: existing?.policy_type || '',
    od_premium_pct: existing?.od || '',
    tp_premium_pct: existing?.tp || '',
    od_reward_pct: existing?.od_reward != null ? String(existing.od_reward) : '',
    tp_reward_pct: existing?.tp_reward != null ? String(existing.tp_reward) : '',
    rto: existing?.rto ?? '',
    state: existing?.state || '',
    city: existing?.city || '',
    zip_code: existing?.zip_code ?? '',
    cc: existing?.cc != null ? String(existing.cc) : '',
    gvw: existing?.gvw != null ? String(existing.gvw) : '',
    ncb: existing?.ncb != null ? String(existing.ncb) : '',
    age: existing?.age != null ? String(existing.age) : '',
    manufactured_year: existing?.manufactured_year != null ? String(existing.manufactured_year) : '',
    seating_capacity: existing?.seating_capacity != null ? String(existing.seating_capacity) : '',
    fuel_type: existing?.fuel_type ?? '',
    make: existing?.make || '',
    model: existing?.model || '',
    variant: existing?.variant ?? '',
    // Board #199 — optional Motor criteria. Every one of them is a filter that
    // applies ONLY when filled: leaving the whole block blank keeps the rate
    // matching exactly the policies it matched before these existed.
    pincode: criterion(existing, 'pincode'),
    cc_from: criterion(existing, 'cc_from'),
    cc_to: criterion(existing, 'cc_to'),
    gvw_from: criterion(existing, 'gvw_from'),
    gvw_to: criterion(existing, 'gvw_to'),
    age_from: criterion(existing, 'age_from'),
    age_to: criterion(existing, 'age_to'),
    seating_capacity_from: criterion(existing, 'seating_capacity_from'),
    seating_capacity_to: criterion(existing, 'seating_capacity_to'),
    vehicle_condition: criterion(existing, 'vehicle_condition'),
    ncb_applicable: ncbApplicableFrom(existing),
    vehicle_category: criterion(existing, 'vehicle_category'),
    vehicle_segment: criterion(existing, 'vehicle_segment'),
  });
  // #406: per band, has the user edited To? Until they have, From copies into To.
  const [toTouched, setToTouched] = useState<Record<BandAxis, boolean>>(() => initialToTouched(form));
  const bandProps = (axis: BandAxis) => {
    const view = bandView(form, axis);
    return {
      from: {
        value: view.from,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          setForm({ ...form, ...editBand(form, axis, 'from', e.target.value, toTouched[axis]) }),
      },
      to: {
        value: view.to,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setToTouched({ ...toTouched, [axis]: true });
          setForm({ ...form, ...editBand(form, axis, 'to', e.target.value, true) });
        },
      },
    };
  };
  const ncbSelected = ncbChoice(form);
  // FIX 30 — full field set for the specific-product scope.
  // When editing, pre-populate from the existing record so the user sees what
  // was previously selected instead of blank dropdowns.
  const [specForm, setSpecForm] = useState<BrokerRateFormState>(
    existing
      ? {
          vehicle_type: existing.vehicle_type || '',
          group: existing.group || '',
          subgroup: existing.subgroup || '',
          policy_type: existing.policy_type || '',
          effective_from: existing.effective_from || new Date().toISOString().slice(0, 10),
          effective_until: existing.effective_until || '',
          // B10: these were hard-coded blank, so an edit showed a rate with no
          // RTO/CC/fuel/... filter when it had one.
          rto: existing.rto ?? '',
          cc: existing.cc != null ? String(existing.cc) : '',
          gvw: existing.gvw != null ? String(existing.gvw) : '',
          fuel_type: existing.fuel_type ?? '',
          od: existing.od || '',
          tp: existing.tp || '',
          reward_to: criterion(existing, 'reward_to'),
          reward_od: criterion(existing, 'reward_od'),
          state: existing.state || '',
          city: existing.city || '',
          pincode: criterion(existing, 'pincode'),
          age: existing.age != null ? String(existing.age) : '',
          manufactured_year: existing.manufactured_year != null ? String(existing.manufactured_year) : '',
          seating_capacity: existing.seating_capacity != null ? String(existing.seating_capacity) : '',
          make: existing.make || '',
          model: existing.model || '',
          variant: existing.variant ?? '',
        }
      : emptySpecificForm,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const setSpec = (patch: Partial<BrokerRateFormState>) => setSpecForm((prev) => ({ ...prev, ...patch }));

  // Master-data lookups (all dynamic).
  const { data: vehicleTypes } = useVehicleTypesLookup();
  const { data: allGroups } = useAllInsuranceGroupsLookup();
  const { data: subgroups } = useInsuranceSubgroupsLookup(specForm.group || undefined);
  // All active policy types — the modal has no insurance-type field in this
  // scope (vehicle_type Motor/Health is a different master), FIX 10.
  const { data: policyTypes } = usePolicyTypesLookup(undefined);
  const { data: fuelTypes } = useFuelTypesLookup();

  // Conditional-mandatory classification semantics (FIX 10/11): a field is
  // required only when its dropdown actually offers options.
  const groupState = classificationFieldState(true, allGroups, 'No group available', 'Select group');
  const subgroupState = classificationFieldState(
    !!specForm.group,
    subgroups,
    'No subgroup available',
    'Select subgroup',
  );
  const policyTypeState = classificationFieldState(
    true,
    policyTypes,
    'No policy type available',
    'Select policy type',
  );
  const { data: states } = useStateLookup();
  const { data: cities } = useCityLookup(specForm.state || undefined);
  const { data: pincodesPage } = usePincodes(1, specForm.city ? { city: specForm.city } : undefined);
  const { data: rtos } = useRTOLookup(specForm.state || undefined);
  const { data: makes } = useVehicleMakes();
  const { data: models } = useVehicleModels(specForm.make || undefined);
  const { data: variants } = useVehicleVariants(specForm.model || undefined);

  // ── Insurance-type form: additional lookups (Task I1) ────────────────
  // #249. The Product dropdown that replaces Group / Subgroup / Policy Type on
  // this form. Narrowed to the chosen insurance type AND this insurer, because
  // the serializer refuses a product whose insurance type disagrees with the
  // rate's -- offering one would be offering a rate that can never match.
  const { data: formProducts } = useProductsLookup(
    { insurance_type: form.insurance_type || undefined, insurer: insurerId },
    { enabled: !!form.insurance_type },
  );
  // Item 10 — the contact picker. Server-side search (global rule 1: debounced,
  // not a request per keystroke) over `/customers/`, which is already scoped to
  // the acting broker by `CustomerViewSet.get_queryset` -- this screen never
  // asks for a broker id and so cannot widen that scope. The API refuses a
  // contact belonging to another broker as well; the picker is the convenience,
  // not the boundary.
  const { debouncedTerm: customerQuery, setTerm: setCustomerQuery } = useDebouncedSearch();
  const { data: customerPage, isLoading: customersLoading } = useCustomers(1, {
    search: customerQuery || undefined,
    page_size: 20,
  });
  const customerOptions = useMemo(
    () =>
      (customerPage?.results ?? []).map((c) => ({
        value: c.id,
        // Two contacts can share a name; the phone is what tells them apart on
        // this screen, exactly as it does in Contacts.
        label: c.phone ? `${c.full_name} — ${c.phone}` : c.full_name,
      })),
    [customerPage],
  );
  const { data: formCities } = useCityLookup(form.state || undefined);
  // The segment vocabulary for the chosen category. Served by the same
  // endpoint the Vehicle Model admin uses, so the two forms can never
  // offer different segment lists for the same category.
  const { data: rateSegments } = useVehicleSegments(form.vehicle_category || undefined);
  const { data: formRtos } = useRTOLookup(form.state || undefined);
  const { data: formPincodes } = usePincodes(1, form.city ? { city: form.city } : undefined);
  const { data: formModels } = useVehicleModels(form.make || undefined);
  const { data: formVariants } = useVehicleVariants(form.model || undefined);

  // Derived: is the selected insurance type "Motor"?
  // B10: falls back to the stored rate's own type name. The insurer-types
  // lookup is still loading on first render (and need not list a type the
  // insurer no longer maps), and a non-Motor verdict here makes Save send
  // `blankMotorCriteria()` -- wiping every criterion the rate had.
  const selectedInsuranceTypeName =
    (insurerTypes || []).find((t) => t.id === form.insurance_type)?.name ??
    (existing && form.insurance_type === existing.insurance_type ? existing.insurance_type_name ?? '' : '');
  const isMotor = selectedInsuranceTypeName.toLowerCase().includes('motor');

  // When both OD% and TP% are filled, Percentage + Commission On become optional.
  const odtpBoth = !!form.od_premium_pct && !!form.tp_premium_pct;

  const handleSubmit = async () => {
    setServerErrors([]);
    if (scopeMode === 'product') {
      const errors = validateBrokerRateForm(specForm, {
        group: groupState.required,
        subgroup: subgroupState.required,
        policy_type: policyTypeState.required,
      });
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      // B10: this branch only opens for a stored rate with a product and no
      // insurance type, so it keeps that product. It used to send
      // `product: null`, stripping the very product that selected this branch
      // (the API now refuses that without an explicit `scope_change`).
      const payload: Record<string, unknown> = {
        insurer: insurerId,
        product: existing?.product ?? null,
        is_active: true,
      };
      // Always-required FK + dates.
      payload.vehicle_type = specForm.vehicle_type;
      payload.effective_from = specForm.effective_from;
      payload.effective_until = specForm.effective_until;
      // Conditionally mandatory classification — sent only when set (FIX 10).
      if (specForm.group) payload.group = specForm.group;
      if (specForm.subgroup) payload.subgroup = specForm.subgroup;
      if (specForm.policy_type) payload.policy_type = specForm.policy_type;
      // Optional dimensions — only sent when filled.
      const optionalKeys: (keyof BrokerRateFormState)[] = [
        'rto',
        'cc',
        'gvw',
        'fuel_type',
        'od',
        'tp',
        'reward_to',
        'reward_od',
        'state',
        'city',
        'pincode',
        'age',
        'manufactured_year',
        'seating_capacity',
        'make',
        'model',
        'variant',
      ];
      for (const key of optionalKeys) {
        const value = specForm[key];
        if (value !== '') payload[key] = value;
      }

      try {
        if (existing) {
          await updateMutation.mutateAsync({ id: existing.id, data: payload });
        } else {
          await createMutation.mutateAsync(payload);
        }
        onClose();
      } catch (err) {
        const parsed = getFieldErrors(err);
        setFieldErrors((prev) => ({ ...prev, ...parsed }));
        setServerErrors(Object.values(parsed));
      }
      return;
    }

    // Expanded insurance-type scope — conditional validation via validateAddRate (Task I1).
    const validationErrors = validateAddRate(
      {
        insurance_type: form.insurance_type,
        effective_from: form.effective_from,
        commission_percentage: form.commission_percentage,
        premium_base: form.premium_base,
        od_premium_pct: form.od_premium_pct,
        tp_premium_pct: form.tp_premium_pct,
      },
    );
    // #406: "With NCB" at 0% would match no policy at all.
    if (isMotor && ncbSelected === 'yes' && form.ncb !== '' && !(Number(form.ncb) > 0)) {
      validationErrors.ncb = 'NCB % must be above 0 for "With NCB". Choose "No NCB" for policies without NCB.';
    }
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload: Record<string, unknown> = {
      insurer: insurerId,
      commission_flat: form.commission_flat || 0,
      reward_percentage: form.reward_percentage || 0,
      max_commission: form.max_commission || null,
      effective_from: form.effective_from,
      effective_until: form.effective_until || null,
      is_active: true,
      insurance_type: form.insurance_type,
    };
    // Omit when blank so the model default (0 / "net_premium") applies server-side.
    if (form.commission_percentage) payload.commission_percentage = form.commission_percentage;
    if (form.premium_base) payload.premium_base = form.premium_base;
    // #249. Product REPLACES Group / Subgroup / Policy Type on this form.
    // `product` is optional: a rate left without one still resolves at the
    // insurance_type tier exactly as before, and one WITH a product resolves at
    // the product tier for the policies carrying it.
    payload.product = form.product || null;
    // Item 10. Sent explicitly, including as an explicit null: this is a PATCH
    // on edit, and omitting the key would make "clear the customer" impossible
    // -- the rate would go on being one person's private rate with nothing on
    // the form saying so.
    payload.customer = form.customer || null;
    // The three classification columns are KEPT in the database and are still
    // required by the CSV and grid import doors (`rate_import.validate_rate_row`),
    // so an EDIT keeps sending whatever the rate already stored -- this is a
    // PATCH, and omitting them would be fine, but sending them makes it
    // impossible for a future change to the modal to blank them by accident.
    // A NEW rate simply has none.
    if (existing) {
      if (form.group) payload.group = form.group;
      if (form.subgroup) payload.subgroup = form.subgroup;
      if (form.policy_type) payload.policy_type = form.policy_type;
    }
    // Motor-only dimensions — only included when Motor type is selected.
    if (isMotor) {
      Object.assign(payload, motorCriteriaPayload(form));
    } else if (existing) {
      // The rate was motor-scoped and is not any more. The update is a PATCH, so
      // criteria left out of the body keep their stored values — a rate that no
      // longer shows a CC band would go on being filtered by one.
      Object.assign(payload, blankMotorCriteria());
    }
    try {
      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      const parsed = getFieldErrors(err);
      setFieldErrors((prev) => ({ ...prev, ...parsed }));
      setServerErrors(Object.values(parsed));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={existing ? 'Edit Rate' : 'Add Rate'} size="lg">
      <div className="space-y-4">
        <ErrorSummary messages={serverErrors} />

        {scopeMode === 'insurance_type' ? (
          <>
            <Select
              label="Insurance Type"
              required
              value={form.insurance_type}
              onChange={(e) => {
                // Every band is cleared below, so From copies into To again.
                setToTouched({ cc: false, gvw: false, age: false, seating_capacity: false });
                setForm({
                  ...form,
                  insurance_type: e.target.value,
                  group: '',
                  subgroup: '',
                  policy_type: '',
                  od_premium_pct: '',
                  tp_premium_pct: '',
                  od_reward_pct: '',
                  tp_reward_pct: '',
                  rto: '',
                  zip_code: '',
                  cc: '',
                  gvw: '',
                  ncb: '',
                  age: '',
                  manufactured_year: '',
                  seating_capacity: '',
                  fuel_type: '',
                  make: '',
                  model: '',
                  variant: '',
                  state: '',
                  city: '',
                  pincode: '',
                  cc_from: '',
                  cc_to: '',
                  gvw_from: '',
                  gvw_to: '',
                  age_from: '',
                  age_to: '',
                  seating_capacity_from: '',
                  seating_capacity_to: '',
                  vehicle_condition: '',
                  ncb_applicable: '',
                });
              }}
              options={withStored(
                (insurerTypes || []).map((t) => ({ value: t.id, label: t.name })),
                form.insurance_type,
                existing?.insurance_type,
                existing?.insurance_type_name,
              )}
              placeholder="Select insurance type"
              error={fieldErrors.insurance_type}
            />

            {/* #249. Product, in place of Group / Subgroup / Policy Type.
                Optional on purpose: a rate with no product still resolves at
                the insurance_type tier, which is where every rate on the live
                book resolves today. Choosing a product narrows the rate to
                policies carrying it. */}
            <Select
              label="Product"
              value={form.product}
              onChange={(e) => setForm({ ...form, product: e.target.value })}
              options={withStored(
                // Broker #4: `mapped_name` is what the country admin named this
                // product FOR THIS INSURER during product mapping -- the label the
                // Available Products tab shows. `name` is the global catalog row,
                // which is what this dropdown used to show and what the report
                // called wrong. The lookup is insurer-scoped above, so
                // `mapped_name` is always the right label; it falls back to the
                // catalog name server-side, so the `|| pr.name` here only covers a
                // cached response served before that field existed.
                (formProducts || []).map((pr) => ({ value: pr.id, label: pr.mapped_name || pr.name })),
                form.product,
                existing?.product,
                existing?.product_name,
              )}
              placeholder={
                form.insurance_type
                  ? 'All products of this insurance type'
                  : 'Select an insurance type first'
              }
              disabled={!form.insurance_type}
              helperText="Leave blank to apply the rate to every product of this insurance type."
              error={fieldErrors.product}
            />

            {/* Item 10 — customer-specific percentage. The per-policy override
                one level up: choosing a contact makes this rate the inflow for
                THAT contact's policies with this insurer, and the agent's
                share-of-broker follows it without being configured again.
                Optional, and blank on every rate written before it existed. */}
            <SearchableSelect
              id="rate-customer"
              label="Customer (optional)"
              value={form.customer}
              onChange={(value) => setForm({ ...form, customer: value })}
              onQueryChange={setCustomerQuery}
              options={withStored(
                customerOptions,
                form.customer,
                existing?.customer,
                existing?.customer_name,
              )}
              placeholder={customersLoading ? 'Loading contacts…' : 'All customers'}
              helperText="Leave blank for the standard rate. Pick a contact to give that customer their own percentage — it replaces this rate card for their policies with this insurer."
              error={fieldErrors.customer}
            />

            {/* Rates & Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Percentage"
                type="number"
                step="0.01"
                required={!odtpBoth}
                value={form.commission_percentage}
                onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })}
                error={fieldErrors.commission_percentage}
              />
              <Select
                label="Commission On"
                required={!odtpBoth}
                value={form.premium_base}
                onChange={(e) => setForm({ ...form, premium_base: e.target.value })}
                options={premiumBaseOptions}
                error={fieldErrors.premium_base}
              />
              <Input
                label="Effective From"
                type="date"
                required
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                error={fieldErrors.effective_from}
              />
              <Input
                label="Effective Until (optional)"
                type="date"
                value={form.effective_until}
                onChange={(e) => setForm({ ...form, effective_until: e.target.value })}
              />
            </div>

            {/* Motor block — visible only when Insurance Type name contains "motor" */}
            {isMotor && (
              <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Motor Dimensions (optional filters)
                </p>

                {/* OD / TP percentages — when both set, Percentage + Commission On become optional */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="OD Premium %"
                    type="number"
                    step="0.01"
                    value={form.od_premium_pct}
                    onChange={(e) => setForm({ ...form, od_premium_pct: e.target.value })}
                  />
                  <Input
                    label="TP Premium %"
                    type="number"
                    step="0.01"
                    value={form.tp_premium_pct}
                    onChange={(e) => setForm({ ...form, tp_premium_pct: e.target.value })}
                  />
                  <Input
                    label="OD Reward %"
                    type="number"
                    step="0.01"
                    value={form.od_reward_pct}
                    onChange={(e) => setForm({ ...form, od_reward_pct: e.target.value })}
                  />
                  <Input
                    label="TP Reward %"
                    type="number"
                    step="0.01"
                    value={form.tp_reward_pct}
                    onChange={(e) => setForm({ ...form, tp_reward_pct: e.target.value })}
                  />
                </div>

                {/* Make / Model / Variant */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select
                    label="Make"
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value, model: '', variant: '' })}
                    options={withStored((makes || []).map((m) => ({ value: m.id, label: m.name })), form.make, existing?.make, existing?.make_name)}
                    placeholder="Select make"
                  />
                  <Select
                    label="Model"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value, variant: '' })}
                    options={withStored((formModels || []).map((m) => ({ value: m.id, label: m.name })), form.model, existing?.model, existing?.model_name)}
                    placeholder={form.make ? 'Select model' : 'Select make first'}
                    disabled={!form.make}
                  />
                  <Select
                    label="Variant"
                    value={form.variant}
                    onChange={(e) => setForm({ ...form, variant: e.target.value })}
                    options={withStored((formVariants || []).map((v) => ({ value: v.id, label: v.name })), form.variant, existing?.variant, criterion(existing, 'variant_name'))}
                    placeholder={form.model ? 'Select variant' : 'Select model first'}
                    disabled={!form.model}
                  />
                </div>

                {/* Vehicle attributes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select
                    label="Fuel Type"
                    value={form.fuel_type}
                    onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}
                    options={withStored((fuelTypes || []).map((f) => ({ value: f.id, label: f.name })), form.fuel_type, existing?.fuel_type, criterion(existing, 'fuel_type_name'))}
                    placeholder="Select fuel type"
                  />
                  {/* #406: CC, GVW, Seating Capacity, Age and NCB are asked
                      once each, under Ranges & Conditions below. */}
                  <Input
                    label="Manufactured Year"
                    type="number"
                    value={form.manufactured_year}
                    onChange={(e) => setForm({ ...form, manufactured_year: e.target.value })}
                  />
                </div>

                {/* Board #199 — ranges and the two yes/no criteria. Every field
                    here narrows the rate ONLY when filled; leave the block alone
                    and the rate keeps matching exactly what it matches today. */}
                <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Ranges &amp; Conditions (optional)
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Bounds are inclusive, and either end can be left open. A policy whose value is
                      not recorded does not match a range you fill.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="CC From"
                      type="number"
                      min={0}
                      {...bandProps('cc').from}
                    />
                    <Input
                      label="CC To"
                      type="number"
                      min={0}
                      {...bandProps('cc').to}
                    />
                    <Input
                      label="GVW From"
                      type="number"
                      step="0.01"
                      min={0}
                      {...bandProps('gvw').from}
                    />
                    <Input
                      label="GVW To"
                      type="number"
                      step="0.01"
                      min={0}
                      {...bandProps('gvw').to}
                    />
                    <Input
                      label="Age From (years)"
                      type="number"
                      min={0}
                      {...bandProps('age').from}
                      helperText="Vehicle age, from its registration date."
                    />
                    <Input
                      label="Age To (years)"
                      type="number"
                      min={0}
                      {...bandProps('age').to}
                    />
                    <Input
                      label="Seating Capacity From"
                      type="number"
                      min={0}
                      {...bandProps('seating_capacity').from}
                    />
                    <Input
                      label="Seating Capacity To"
                      type="number"
                      min={0}
                      {...bandProps('seating_capacity').to}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select
                      label="Vehicle Condition"
                      value={form.vehicle_condition}
                      onChange={(e) => setForm({ ...form, vehicle_condition: e.target.value })}
                      options={vehicleConditionOptions}
                    />
                    <Select
                      label="NCB"
                      value={ncbSelected}
                      onChange={(e) => setForm({ ...form, ...editNcbChoice(e.target.value) })}
                      options={ncbApplicableOptions}
                      helperText="Whether the policy carries any NCB at all."
                    />
                    {ncbSelected === 'yes' && (
                      <Input
                        label="NCB %"
                        type="number"
                        min={1}
                        max={100}
                        value={form.ncb}
                        onChange={(e) => setForm({ ...form, ncb: e.target.value })}
                        error={fieldErrors.ncb}
                        helperText="Optional — leave blank for any NCB above 0%."
                      />
                    )}
                    {/* Category and segment. These let one rule cover "all
                        SUVs" or "all two-wheelers" instead of being entered
                        model by model. The segment vocabulary depends on the
                        category, so changing the category clears a segment
                        that no longer belongs to it. */}
                    <Select
                      label="Vehicle Category"
                      value={form.vehicle_category}
                      onChange={(e) =>
                        setForm({ ...form, vehicle_category: e.target.value, vehicle_segment: '' })
                      }
                      options={vehicleCategoryOptions}
                    />
                    <Select
                      label="Vehicle Segment"
                      value={form.vehicle_segment}
                      onChange={(e) => setForm({ ...form, vehicle_segment: e.target.value })}
                      options={[
                        { value: '', label: 'Any segment' },
                        ...withStored(
                          (rateSegments || []).map((seg) => ({ value: seg, label: seg })),
                          form.vehicle_segment,
                          criterion(existing, 'vehicle_segment'),
                          criterion(existing, 'vehicle_segment'),
                        ),
                      ]}
                      disabled={!form.vehicle_category}
                      helperText={
                        form.vehicle_category
                          ? undefined
                          : 'Pick a category first — segments differ by category.'
                      }
                    />
                  </div>
                </div>

                {/* Geography. Each of these is an OPTIONAL filter, so every one
                    carries an explicit "Any" option: the shared Select renders
                    its `placeholder` as a DISABLED option, so a location picked
                    by mistake could otherwise never be taken back off the rate.
                    The placeholder is kept only as the "pick the parent first"
                    hint, where there is nothing to choose from yet. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Select
                    label="State"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value, city: '', rto: '', pincode: '' })}
                    options={[
                      { value: '', label: 'Any state' },
                      ...withStored((states || []).map((s) => ({ value: s.id, label: s.name })), form.state, existing?.state, existing?.state_name),
                    ]}
                  />
                  <Select
                    label="City"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value, pincode: '' })}
                    options={
                      form.state
                        ? [
                            { value: '', label: 'Any city' },
                            ...withStored((formCities || []).map((c) => ({ value: c.id, label: c.name })), form.city, existing?.city, existing?.city_name),
                          ]
                        : []
                    }
                    placeholder={form.state ? undefined : 'Select state first'}
                    disabled={!form.state}
                  />
                  <Input
                    label="ZIP Code"
                    value={form.zip_code}
                    onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                    placeholder="e.g. 400001"
                  />
                  <Select
                    label="RTO"
                    value={form.rto}
                    onChange={(e) => setForm({ ...form, rto: e.target.value })}
                    options={
                      form.state
                        ? [
                            { value: '', label: 'Any RTO' },
                            ...withStored(
                              (formRtos || []).map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` })),
                              form.rto,
                              existing?.rto,
                              criterion(existing, 'rto_name'),
                            ),
                          ]
                        : []
                    }
                    placeholder={form.state ? undefined : 'Select state first'}
                    disabled={!form.state}
                  />
                  <Select
                    label="Pincode"
                    value={form.pincode}
                    onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                    options={
                      form.city
                        ? [
                            { value: '', label: 'Any pincode' },
                            ...withStored((formPincodes?.results || []).map((p) => ({ value: p.id, label: p.code })), form.pincode, criterion(existing, 'pincode'), criterion(existing, 'pincode_code')),
                          ]
                        : []
                    }
                    placeholder={form.city ? undefined : 'Select city first'}
                    disabled={!form.city}
                    helperText="Matched against the policy holder's pincode."
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Vehicle Type, From Date and To Date are required. Group, Subgroup and Policy Type are
              required when options are available. All other fields are optional filters.
            </p>

            {/* Classification */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Vehicle Type"
                required
                value={specForm.vehicle_type}
                onChange={(e) => setSpec({ vehicle_type: e.target.value })}
                options={withStored((vehicleTypes || []).map((t) => ({ value: t.id, label: t.name })), specForm.vehicle_type, existing?.vehicle_type, existing?.vehicle_type_name)}
                placeholder="Select vehicle type"
                error={fieldErrors.vehicle_type}
              />
              <Select
                label="Group"
                required={groupState.required}
                value={specForm.group}
                onChange={(e) => setSpec({ group: e.target.value, subgroup: '' })}
                options={withStored((allGroups || []).map((g) => ({ value: g.id, label: g.name })), specForm.group, existing?.group, existing?.group_name)}
                placeholder={groupState.placeholder}
                disabled={groupState.disabled}
                error={fieldErrors.group}
              />
              <Select
                label="Subgroup"
                required={subgroupState.required}
                value={specForm.subgroup}
                onChange={(e) => setSpec({ subgroup: e.target.value })}
                options={withStored((subgroups || []).map((s) => ({ value: s.id, label: s.name })), specForm.subgroup, existing?.subgroup, existing?.subgroup_name)}
                placeholder={subgroupState.placeholder}
                disabled={subgroupState.disabled}
                error={fieldErrors.subgroup}
              />
              <Select
                label="Policy Type"
                required={policyTypeState.required}
                value={specForm.policy_type}
                onChange={(e) => setSpec({ policy_type: e.target.value })}
                options={withStored((policyTypes || []).map((p) => ({ value: p.id, label: p.name })), specForm.policy_type, existing?.policy_type, existing?.policy_type_name)}
                placeholder={policyTypeState.placeholder}
                disabled={policyTypeState.disabled}
                error={fieldErrors.policy_type}
              />
            </div>

            {/* Vehicle attributes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Make"
                value={specForm.make}
                onChange={(e) => setSpec({ make: e.target.value, model: '', variant: '' })}
                options={withStored((makes || []).map((m) => ({ value: m.id, label: m.name })), specForm.make, existing?.make, existing?.make_name)}
                placeholder="Select make"
              />
              <Select
                label="Model"
                value={specForm.model}
                onChange={(e) => setSpec({ model: e.target.value, variant: '' })}
                options={withStored((models || []).map((m) => ({ value: m.id, label: m.name })), specForm.model, existing?.model, existing?.model_name)}
                placeholder={specForm.make ? 'Select model' : 'Select make first'}
                disabled={!specForm.make}
              />
              <Select
                label="Variant"
                value={specForm.variant}
                onChange={(e) => setSpec({ variant: e.target.value })}
                options={withStored((variants || []).map((v) => ({ value: v.id, label: v.name })), specForm.variant, existing?.variant, criterion(existing, 'variant_name'))}
                placeholder={specForm.model ? 'Select variant' : 'Select model first'}
                disabled={!specForm.model}
              />
              <Select
                label="Fuel Type"
                value={specForm.fuel_type}
                onChange={(e) => setSpec({ fuel_type: e.target.value })}
                options={withStored((fuelTypes || []).map((f) => ({ value: f.id, label: f.name })), specForm.fuel_type, existing?.fuel_type, criterion(existing, 'fuel_type_name'))}
                placeholder="Select fuel type"
              />
              <Input
                label="CC"
                type="number"
                value={specForm.cc}
                onChange={(e) => setSpec({ cc: e.target.value })}
              />
              <Input
                label="GVW"
                type="number"
                step="0.01"
                value={specForm.gvw}
                onChange={(e) => setSpec({ gvw: e.target.value })}
              />
              <Input
                label="Seating Capacity"
                type="number"
                value={specForm.seating_capacity}
                onChange={(e) => setSpec({ seating_capacity: e.target.value })}
              />
              <Input
                label="Manufactured Year"
                type="number"
                value={specForm.manufactured_year}
                onChange={(e) => setSpec({ manufactured_year: e.target.value })}
              />
              <Input
                label="Age"
                type="number"
                value={specForm.age}
                onChange={(e) => setSpec({ age: e.target.value })}
              />
            </div>

            {/* Geography */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="State"
                value={specForm.state}
                onChange={(e) => setSpec({ state: e.target.value, city: '', pincode: '', rto: '' })}
                options={withStored((states || []).map((s) => ({ value: s.id, label: s.name })), specForm.state, existing?.state, existing?.state_name)}
                placeholder="Select state"
              />
              <Select
                label="City"
                value={specForm.city}
                onChange={(e) => setSpec({ city: e.target.value, pincode: '' })}
                options={withStored((cities || []).map((c) => ({ value: c.id, label: c.name })), specForm.city, existing?.city, existing?.city_name)}
                placeholder={specForm.state ? 'Select city' : 'Select state first'}
                disabled={!specForm.state}
              />
              <Select
                label="ZIP / Pincode"
                value={specForm.pincode}
                onChange={(e) => setSpec({ pincode: e.target.value })}
                options={withStored((pincodesPage?.results || []).map((p) => ({ value: p.id, label: p.code })), specForm.pincode, criterion(existing, 'pincode'), criterion(existing, 'pincode_code'))}
                placeholder={specForm.city ? 'Select pincode' : 'Select city first'}
                disabled={!specForm.city}
              />
              <Select
                label="RTO"
                value={specForm.rto}
                onChange={(e) => setSpec({ rto: e.target.value })}
                options={withStored((rtos || []).map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` })), specForm.rto, existing?.rto, criterion(existing, 'rto_name'))}
                placeholder={specForm.state ? 'Select RTO' : 'Select state first'}
                disabled={!specForm.state}
              />
            </div>

            {/* Rate values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="OD"
                type="number"
                step="0.01"
                value={specForm.od}
                onChange={(e) => setSpec({ od: e.target.value })}
              />
              <Input
                label="TP"
                type="number"
                step="0.01"
                value={specForm.tp}
                onChange={(e) => setSpec({ tp: e.target.value })}
              />
              <Input
                label="Reward TO"
                type="number"
                step="0.01"
                value={specForm.reward_to}
                onChange={(e) => setSpec({ reward_to: e.target.value })}
              />
              <Input
                label="Reward OD"
                type="number"
                step="0.01"
                value={specForm.reward_od}
                onChange={(e) => setSpec({ reward_od: e.target.value })}
              />
            </div>

            {/* Validity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="From Date"
                type="date"
                required
                value={specForm.effective_from}
                onChange={(e) => setSpec({ effective_from: e.target.value })}
                error={fieldErrors.effective_from}
              />
              <Input
                label="To Date"
                type="date"
                required
                value={specForm.effective_until}
                onChange={(e) => setSpec({ effective_until: e.target.value })}
                error={fieldErrors.effective_until}
              />
            </div>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        >
          {existing ? 'Save' : 'Add Rate'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// =====================================================================
// Upload tab — CSV monthly grid
// =====================================================================

function UploadTab({ insurerId, insurerName }: { insurerId: string; insurerName: string }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<BrokerRateCsvUploadResult | null>(null);
  const uploadMutation = useUploadBrokerRatesCsv();

  const downloadTemplate = async () => {
    try {
      const blob = await commissionService.downloadBrokerRateTemplate(insurerId || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = insurerName
        ? `commission_rates_${insurerName.replace(/\s+/g, '_')}.csv`
        : 'broker_commission_rates_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const handleFile = async (file: File) => {
    const data = await uploadMutation.mutateAsync({ file, insurerId: insurerId || undefined });
    setResult(data);
  };

  return (
    <Card padding="md">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Monthly Grid Upload{insurerName ? ` — ${insurerName}` : ''}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {insurerName
              ? `Download the ${insurerName} template (pre-filled with their mapped insurance types), fill in the rates, and upload it back. Rows are created for ${insurerName} and your broker.`
              : 'Drop the CSV you got from the insurer this month. Each row creates a commission rate scoped to your broker.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={downloadTemplate}>
            Download Template
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              if (fileRef.current) fileRef.current.value = '';
            }}
          />
          <Button
            variant="primary"
            leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
            onClick={() => fileRef.current?.click()}
            isLoading={uploadMutation.isPending}
          >
            Upload CSV
          </Button>
        </div>

        {result && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm">
            <p>
              <strong>{result.created}</strong> rate(s) imported.{' '}
              {result.error_count > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  <strong>{result.error_count}</strong> row(s) skipped (see below).
                </span>
              )}
            </p>
            {result.errors.length > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-1 text-left">Row</th>
                      <th className="px-2 py-1 text-left">Error</th>
                      <th className="px-2 py-1 text-left">Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {result.errors.map((e) => (
                      <tr key={e.row}>
                        <td className="px-2 py-1">{e.row}</td>
                        <td className="px-2 py-1 text-red-600 dark:text-red-400">{e.error}</td>
                        <td className="px-2 py-1 font-mono">{JSON.stringify(e.data)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// =====================================================================
// Slabs tab
// =====================================================================

function SlabsTab({ insurerId }: { insurerId: string }) {
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<CommissionSlabRecord | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { data, isLoading } = useCommissionSlabs(page, { insurer: insurerId, is_active: 'true' });
  const deleteMutation = useDeleteSlab();
  // Cast at the hook boundary: the hook is typed by the frozen `types/index.ts`,
  // which does not yet carry #276's product / basis / criteria fields.
  const rows = data?.results || [];

  return (
    <Card padding="none">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Revenue slabs. A slab&apos;s tier is chosen from the broker&apos;s{' '}
          <strong>cumulative net premium with this insurer in the period</strong> (issued and
          active policies). A bonus slab ADDS its bonus % to the grid commission; a tier-rate
          slab REPLACES the grid rate with its commission %.
        </p>
        <Button
          variant="primary"
          leftIcon={<PlusIcon className="w-4 h-4" />}
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
        >
          Add Slab
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">No slabs configured for this insurer.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['Order', 'Name', 'Product', 'Basis', 'Criteria', 'Metric', 'Period', 'Min', 'Max', 'Commission %', 'Bonus %', 'Effective', ''].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 text-sm">{s.slab_order}</td>
                  <td className="px-4 py-3 text-sm font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-sm">{s.product_name || 'All products'}</td>
                  <td className="px-4 py-3 text-sm">{SLAB_BASIS_LABELS[s.basis] || titleCase(s.basis || '')}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{slabCriteriaSummary(s) || '—'}</td>
                  <td className="px-4 py-3 text-sm">{titleCase(s.metric)}</td>
                  <td className="px-4 py-3 text-sm">{titleCase(s.period)}</td>
                  <td className="px-4 py-3 text-sm">{s.min_value}</td>
                  <td className="px-4 py-3 text-sm">{s.max_value || '∞'}</td>
                  <td className="px-4 py-3 text-sm">{s.commission_percentage}%</td>
                  <td className="px-4 py-3 text-sm">{s.bonus_percentage}%</td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(s.effective_from)}
                    {s.effective_until ? ` → ${formatDate(s.effective_until)}` : ''}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditing(s);
                        setShowModal(true);
                      }}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Remove this slab?')) deleteMutation.mutate(s.id);
                      }}
                    >
                      <TrashIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.count > 20 && (
        <div className="flex items-center justify-between p-4 text-sm border-t border-gray-100 dark:border-gray-700">
          <span>Page {page}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={rows.length < 20}>
              Next
            </Button>
          </div>
        </div>
      )}

      {showModal && (
        <SlabFormModal insurerId={insurerId} existing={editing} onClose={() => setShowModal(false)} />
      )}
    </Card>
  );
}

function SlabFormModal({
  insurerId,
  existing,
  onClose,
}: {
  insurerId: string;
  existing: CommissionSlabRecord | null;
  onClose: () => void;
}) {
  const { data: insuranceTypes } = useInsuranceTypesLookup();
  const createMutation = useCreateSlab();
  const updateMutation = useUpdateSlab();
  const [form, setForm] = useState({
    name: existing?.name || '',
    slab_order: existing?.slab_order ?? 0,
    insurance_type: existing?.insurance_type || '',
    product: existing?.product || '',
    basis: (existing?.basis || 'bonus_on_commission') as SlabBasis,
    vehicle_condition: existing?.vehicle_condition || '',
    // Tri-state on the wire (true / false / null), so it is held as a string
    // here and mapped back on submit -- `''` is "either", which is NOT the
    // same as `false` ("only policies with no NCB").
    ncb_applicable:
      existing?.ncb_applicable === true ? 'yes' : existing?.ncb_applicable === false ? 'no' : '',
    metric: (existing?.metric || 'premium') as CommissionSlabRecord['metric'],
    period: (existing?.period || 'monthly') as CommissionSlabRecord['period'],
    min_value: existing?.min_value || '',
    max_value: existing?.max_value || '',
    commission_percentage: existing?.commission_percentage || '',
    bonus_percentage: existing?.bonus_percentage || '0',
    effective_from: existing?.effective_from || new Date().toISOString().slice(0, 10),
    effective_until: existing?.effective_until || '',
  });
  // #276. Products of the chosen insurance type for this insurer -- the same
  // pairing the serializer enforces, so the dropdown cannot offer a product
  // the server will refuse.
  const { data: slabProducts } = useProductsLookup(
    { insurance_type: form.insurance_type || undefined, insurer: insurerId },
    { enabled: !!form.insurance_type },
  );

  const handleSubmit = async () => {
    if (!form.name || !form.commission_percentage || !form.min_value) {
      toast.error('Name, min value and commission % are required');
      return;
    }
    const payload: Record<string, unknown> = {
      insurer: insurerId,
      insurance_type: form.insurance_type || null,
      product: form.product || null,
      basis: form.basis,
      vehicle_condition: form.vehicle_condition,
      ncb_applicable:
        form.ncb_applicable === 'yes' ? true : form.ncb_applicable === 'no' ? false : null,
      name: form.name,
      slab_order: form.slab_order,
      metric: form.metric,
      period: form.period,
      min_value: form.min_value,
      max_value: form.max_value || null,
      commission_percentage: form.commission_percentage,
      bonus_percentage: form.bonus_percentage || 0,
      effective_from: form.effective_from,
      effective_until: form.effective_until || null,
      is_active: true,
    };
    try {
      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      /* toast surfaced by hook */
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={existing ? 'Edit Slab' : 'Add Slab'} size="lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Slab Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Order"
          type="number"
          value={String(form.slab_order)}
          onChange={(e) => setForm({ ...form, slab_order: parseInt(e.target.value, 10) || 0 })}
        />
        <Select
          label="Insurance Type (optional)"
          value={form.insurance_type}
          onChange={(e) => setForm({ ...form, insurance_type: e.target.value, product: '' })}
          options={[
            { value: '', label: 'All types' },
            ...(insuranceTypes || []).map((t) => ({ value: t.id, label: t.name })),
          ]}
        />
        <Select
          label="Product (optional)"
          value={form.product}
          onChange={(e) => setForm({ ...form, product: e.target.value })}
          options={[
            { value: '', label: 'All products of this type' },
            // Broker #4: the mapped name, exactly as the rate form above -- the
            // two dropdowns sit on one screen and must not label one product
            // two ways.
            ...(slabProducts || []).map((pr) => ({ value: pr.id, label: pr.mapped_name || pr.name })),
          ]}
          disabled={!form.insurance_type}
          helperText="Only this product's premium counts toward the slab, and only its policies are paid under it."
        />
        <Select
          label="Basis"
          value={form.basis}
          onChange={(e) => setForm({ ...form, basis: e.target.value as SlabBasis })}
          options={(Object.keys(SLAB_BASIS_LABELS) as SlabBasis[]).map((b) => ({
            value: b,
            label: SLAB_BASIS_LABELS[b],
          }))}
          helperText={
            form.basis === 'rate_of_net_premium'
              ? "Commission % below REPLACES the grid rate for policies that reach this tier."
              : 'Bonus % below is ADDED to the commission the grid already computed.'
          }
        />
        <Select
          label="Vehicle Condition (optional)"
          value={form.vehicle_condition}
          onChange={(e) =>
            setForm({ ...form, vehicle_condition: e.target.value as CommissionSlabRecord['vehicle_condition'] })
          }
          options={[
            { value: '', label: 'New or rollover' },
            { value: 'new', label: 'New vehicles only' },
            { value: 'old', label: 'Rollover vehicles only' },
          ]}
        />
        <Select
          label="NCB (optional)"
          value={form.ncb_applicable}
          onChange={(e) => setForm({ ...form, ncb_applicable: e.target.value })}
          options={[
            { value: '', label: 'Any' },
            { value: 'yes', label: 'With NCB' },
            { value: 'no', label: 'Without NCB' },
          ]}
        />
        <Select
          label="Metric"
          value={form.metric}
          onChange={(e) => setForm({ ...form, metric: e.target.value as CommissionSlabRecord['metric'] })}
          options={[
            { value: 'premium', label: 'Premium Volume' },
            { value: 'policy_count', label: 'Policy Count' },
            { value: 'lives_covered', label: 'Lives Covered' },
          ]}
        />
        <Select
          label="Period"
          value={form.period}
          onChange={(e) => setForm({ ...form, period: e.target.value as CommissionSlabRecord['period'] })}
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'yearly', label: 'Yearly' },
          ]}
        />
        <Input
          label="Min Value"
          type="number"
          step="0.01"
          required
          value={form.min_value}
          onChange={(e) => setForm({ ...form, min_value: e.target.value })}
        />
        <Input
          label="Max Value (optional)"
          type="number"
          step="0.01"
          value={form.max_value}
          onChange={(e) => setForm({ ...form, max_value: e.target.value })}
        />
        <Input
          label="Commission %"
          type="number"
          step="0.01"
          required
          value={form.commission_percentage}
          onChange={(e) => setForm({ ...form, commission_percentage: e.target.value })}
        />
        <Input
          label="Bonus %"
          type="number"
          step="0.01"
          value={form.bonus_percentage}
          onChange={(e) => setForm({ ...form, bonus_percentage: e.target.value })}
        />
        <Input
          label="Effective From"
          type="date"
          required
          value={form.effective_from}
          onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
        />
        <Input
          label="Effective Until (optional)"
          type="date"
          value={form.effective_until}
          onChange={(e) => setForm({ ...form, effective_until: e.target.value })}
        />
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        >
          {existing ? 'Save' : 'Add Slab'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// =====================================================================
// Waterfall tab (read-only)
// =====================================================================

function WaterfallTab({ insurerId }: { insurerId: string }) {
  const { data: insuranceTypes } = useInsuranceTypesLookup();
  const { data, isLoading } = useCommissionWaterfalls({ insurer: insurerId, is_active: 'true' });
  const rows = data?.results || [];
  const insurerTypeMap = useMemo(
    () => Object.fromEntries((insuranceTypes || []).map((t) => [t.id, t.name])),
    [insuranceTypes],
  );

  return (
    <Card padding="none">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          How total commission from this insurer splits between platform, broker, POSP and super POSP. Set by
          the platform — broker can view only.
        </p>
      </div>
      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No waterfall configured for this insurer. The calculator falls back to platform 5% / broker 95%.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['Insurance Type', 'Name', 'Platform', 'Broker', 'POSP', 'Super POSP', 'Effective'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((w) => (
                <tr key={w.id}>
                  <td className="px-4 py-3 text-sm">
                    {w.insurance_type_name || insurerTypeMap[w.insurance_type] || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">{w.name}</td>
                  <td className="px-4 py-3 text-sm">{w.platform_share}%</td>
                  <td className="px-4 py-3 text-sm">{w.broker_share}%</td>
                  <td className="px-4 py-3 text-sm">{w.posp_share}%</td>
                  <td className="px-4 py-3 text-sm">{w.super_posp_share}%</td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(w.effective_from)}
                    {w.effective_until ? ` → ${formatDate(w.effective_until)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// =====================================================================
// History tab
// =====================================================================

function HistoryTab({ insurerId }: { insurerId: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useBrokerRates(page, { insurer: insurerId, is_active: 'false' });
  const rows = data?.results || [];

  return (
    <Card padding="none">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Past / inactive rates that no longer apply. Kept for audit.
        </p>
      </div>
      {isLoading ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">No historical rates.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['Scope', 'Percentage', 'Premium Base', 'Effective From', 'Effective Until'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 text-sm">{r.product_name || r.insurance_type_name || '—'}</td>
                  <td className="px-4 py-3 text-sm">{r.commission_percentage}%</td>
                  <td className="px-4 py-3 text-sm">{titleCase(r.premium_base)}</td>
                  <td className="px-4 py-3 text-sm">{formatDate(r.effective_from)}</td>
                  <td className="px-4 py-3 text-sm">{r.effective_until ? formatDate(r.effective_until) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.count > 20 && (
        <div className="flex items-center justify-between p-4 text-sm border-t border-gray-100 dark:border-gray-700">
          <span>Page {page}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={rows.length < 20}>
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// Re-export for App.tsx import
export default BrokerInsurerCommissionsPage;
